import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  Row,
  Col,
  Button,
  Spinner,
  Alert,
  Form,
  Badge,
  InputGroup,
  Modal,
  Table,
} from "react-bootstrap";
import { useAuth } from "../../contexts/AuthContext";
import { fires } from "../../firebase";
import firebase from "firebase";
import {
  FaStar,
  FaCalendar,
  FaMapMarkerAlt,
  FaImage,
  FaSearch,
  FaSync,
  FaCheckCircle,
} from "react-icons/fa";

export default function AdminEventManagement() {
  const { currentUser } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterFeatured, setFilterFeatured] = useState("all"); // "all", "featured", "not-featured"
  
  // Featured event management
  const [showFeaturedModal, setShowFeaturedModal] = useState(false);
  const [editingFeatured, setEditingFeatured] = useState(null);
  const [featuredFormData, setFeaturedFormData] = useState({
    title: "",
    photoURL: "",
    description: "",
  });
  const [featuredEvents, setFeaturedEvents] = useState([]);
  const [loadingFeatured, setLoadingFeatured] = useState(false);

  useEffect(() => {
    checkAdminAccess();
  }, [currentUser]);

  useEffect(() => {
    if (isAdmin) {
      loadEvents();
      loadFeaturedEvents();
    }
  }, [isAdmin]);

  useEffect(() => {
    filterEvents();
  }, [events, searchTerm, filterFeatured]);

  const checkAdminAccess = async () => {
    try {
      if (!currentUser) {
        setError("Please log in to access this page");
        setLoading(false);
        return;
      }

      const adminDoc = await fires.collection("admin").doc(currentUser.uid).get();
      if (!adminDoc.exists) {
        setError("Access denied. Admin privileges required.");
        setLoading(false);
        return;
      }

      const adminData = adminDoc.data();
      if (!adminData.roles || !adminData.roles.includes("management")) {
        setError("Access denied. Management role required.");
        setLoading(false);
        return;
      }

      setIsAdmin(true);
      setLoading(false);
    } catch (err) {
      console.error("Error checking admin access:", err);
      setError("Error verifying admin access");
      setLoading(false);
    }
  };

  const loadEvents = async () => {
    try {
      setLoading(true);
      setError("");

      const now = firebase.firestore.Timestamp.fromDate(new Date());

      // Load events where approved=true, type='event', status='live', and endDate >= now (not past events)
      let eventsSnapshot;
      try {
        // Try to use compound query if index exists
        eventsSnapshot = await fires
          .collection("events")
          .where("approved", "==", true)
          .where("type", "==", "event")
          .where("status", "==", "live")
          .where("endDate", ">=", now)
          .orderBy("endDate", "asc")
          .limit(500)
          .get();
      } catch (queryErr) {
        // If compound query fails (index doesn't exist), load with simpler query and filter in memory
        console.log("Compound query failed, using fallback filter:", queryErr);
        try {
          // Try simpler query with fewer filters
          eventsSnapshot = await fires
            .collection("events")
            .where("approved", "==", true)
            .where("status", "==", "live")
            .where("endDate", ">=", now)
            .orderBy("endDate", "asc")
            .limit(500)
            .get();
        } catch (queryErr2) {
          // Last resort: load all approved and filter in memory
          console.log("Simpler query also failed, using basic query:", queryErr2);
          eventsSnapshot = await fires
            .collection("events")
            .where("approved", "==", true)
            .orderBy("timestamp", "desc")
            .limit(500)
            .get();
        }
      }

      const eventsData = eventsSnapshot.docs
        .map((doc) => {
          const data = doc.data();
          
          return {
            ...data,
            id: doc.id,
            title: data.title || data.name || "Untitled Event",
            image: data.photoURL || data.image || data.photo || null,
            startDate: data.startDate || null,
            endDate: data.endDate || null,
            location: data.address?.fullAddress || "No location",
            featured: data.featured === true,
            approved: data.approved === true,
            status: data.status || "unknown",
          };
        })
        .filter((event) => {
          // Filter: only include events where type === 'event' and status === 'live'
          if (event.type !== 'event') {
            return false;
          }
          
          if (event.status !== "live") {
            return false;
          }

          // Additional safety check: ensure endDate >= now (exclude past events)
          if (!event.endDate) {
            return false; // Skip events without endDate
          }

          const endDate = event.endDate?.toDate ? event.endDate.toDate() : null;
          const nowDate = new Date();

          if (!endDate) {
            return false;
          }

          // Only include events that haven't ended yet
          return endDate >= nowDate;
        });

      setEvents(eventsData);
      setFilteredEvents(eventsData);
    } catch (err) {
      console.error("Error loading events:", err);
      setError("Error loading events: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const filterEvents = () => {
    let filtered = [...events];

    // Filter by search term
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (event) =>
          event.title?.toLowerCase().includes(searchLower) ||
          event.location?.toLowerCase().includes(searchLower)
      );
    }

    // Filter by featured status
    if (filterFeatured === "featured") {
      filtered = filtered.filter((event) => event.featured === true);
    } else if (filterFeatured === "not-featured") {
      filtered = filtered.filter((event) => event.featured !== true);
    }

    setFilteredEvents(filtered);
  };

  const toggleFeatured = async (eventId, currentFeaturedStatus) => {
    try {
      setSaving(true);
      setError("");
      setSuccess("");

      const newFeaturedStatus = !currentFeaturedStatus;

      // Update event in Firestore
      await fires.collection("events").doc(eventId).update({
        featured: newFeaturedStatus,
        timestamp: firebase.firestore.Timestamp.fromDate(new Date()),
      });

      // Update local state
      setEvents((prevEvents) =>
        prevEvents.map((event) =>
          event.id === eventId
            ? { ...event, featured: newFeaturedStatus }
            : event
        )
      );

      setSuccess(
        `Event ${newFeaturedStatus ? "featured" : "unfeatured"} successfully!`
      );
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("Error updating featured status:", err);
      setError("Error updating featured status: " + err.message);
      setTimeout(() => setError(""), 5000);
    } finally {
      setSaving(false);
    }
  };

  const toggleApproved = async (eventId, currentApprovedStatus) => {
    try {
      setSaving(true);
      setError("");
      setSuccess("");

      const newApprovedStatus = !currentApprovedStatus;

      // Update event in Firestore
      await fires.collection("events").doc(eventId).update({
        approved: newApprovedStatus,
        timestamp: firebase.firestore.Timestamp.fromDate(new Date()),
      });

      // Update local state
      setEvents((prevEvents) =>
        prevEvents.map((event) =>
          event.id === eventId
            ? { ...event, approved: newApprovedStatus }
            : event
        )
      );

      setSuccess(
        `Event ${newApprovedStatus ? "approved" : "unapproved"} successfully!`
      );
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("Error updating approved status:", err);
      setError("Error updating approved status: " + err.message);
      setTimeout(() => setError(""), 5000);
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return "No date";
    
    if (date.toDate) {
      return date.toDate().toLocaleString();
    } else if (date.toMillis) {
      return new Date(date.toMillis()).toLocaleString();
    } else if (typeof date === "string") {
      return date;
    } else if (date instanceof Date) {
      return date.toLocaleString();
    }
    return "No date";
  };

  const loadFeaturedEvents = async () => {
    try {
      setLoadingFeatured(true);
      const featuredSnapshot = await fires
        .collection("featured_events")
        .orderBy("timestamp", "desc")
        .get();

      const featuredData = featuredSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setFeaturedEvents(featuredData);
    } catch (err) {
      console.error("Error loading featured events:", err);
      setError("Error loading featured events: " + err.message);
    } finally {
      setLoadingFeatured(false);
    }
  };

  const handleOpenFeaturedModal = (featuredEvent = null) => {
    if (featuredEvent) {
      setEditingFeatured(featuredEvent.id);
      setFeaturedFormData({
        title: featuredEvent.title || "",
        photoURL: featuredEvent.photoURL || "",
        description: featuredEvent.description || "",
      });
    } else {
      setEditingFeatured(null);
      setFeaturedFormData({
        title: "",
        photoURL: "",
        description: "",
      });
    }
    setShowFeaturedModal(true);
  };

  const handleCloseFeaturedModal = () => {
    setShowFeaturedModal(false);
    setEditingFeatured(null);
    setFeaturedFormData({
      title: "",
      photoURL: "",
      description: "",
    });
  };

  const handleSaveFeatured = async () => {
    try {
      setSaving(true);
      setError("");
      setSuccess("");

      if (!featuredFormData.title.trim()) {
        setError("Title is required");
        return;
      }

      const featuredData = {
        title: featuredFormData.title.trim(),
        photoURL: featuredFormData.photoURL.trim(),
        description: featuredFormData.description.trim(),
        timestamp: firebase.firestore.Timestamp.fromDate(new Date()),
      };

      if (editingFeatured) {
        // Update existing featured event
        await fires
          .collection("featured_events")
          .doc(editingFeatured)
          .update(featuredData);
        setSuccess("Featured event updated successfully!");
      } else {
        // Create new featured event
        await fires.collection("featured_events").add(featuredData);
        setSuccess("Featured event created successfully!");
      }

      await loadFeaturedEvents();
      handleCloseFeaturedModal();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("Error saving featured event:", err);
      setError("Error saving featured event: " + err.message);
      setTimeout(() => setError(""), 5000);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFeatured = async (featuredId) => {
    if (!window.confirm("Are you sure you want to delete this featured event?")) {
      return;
    }

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      await fires.collection("featured_events").doc(featuredId).delete();
      await loadFeaturedEvents();
      setSuccess("Featured event deleted successfully!");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("Error deleting featured event:", err);
      setError("Error deleting featured event: " + err.message);
      setTimeout(() => setError(""), 5000);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container className="py-5">
        <div className="d-flex justify-content-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  if (!isAdmin) {
    return (
      <Container className="py-5">
        <Alert variant="danger">
          <Alert.Heading>Access Denied</Alert.Heading>
          <p>{error || "You do not have permission to access this page."}</p>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="mb-1">
                <FaStar className="me-2" />
                Event Management
              </h2>
              <p className="text-muted mb-0">
                Manage featured events - Toggle featured status for events
              </p>
            </div>
            <Button
              variant="outline-primary"
              onClick={loadEvents}
              disabled={loading}
            >
              <FaSync className={loading ? "fa-spin me-2" : "me-2"} />
              Refresh
            </Button>
          </div>
        </Col>
      </Row>

      {/* Alerts */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      {/* Featured Events Management Section */}
      <Card className="mb-4 border-0 shadow-sm">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            <FaStar className="me-2" />
            Featured Events Management
          </h5>
          <Button
            variant="primary"
            size="sm"
            onClick={() => handleOpenFeaturedModal()}
          >
            <FaStar className="me-2" />
            Add Featured Event
          </Button>
        </Card.Header>
        <Card.Body>
          {loadingFeatured ? (
            <div className="text-center py-3">
              <Spinner animation="border" size="sm" />
            </div>
          ) : featuredEvents.length === 0 ? (
            <Alert variant="info" className="mb-0">
              No featured events found. Click "Add Featured Event" to create one.
            </Alert>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Image</th>
                  <th>Title</th>
                  <th>Description</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {featuredEvents.map((featured) => (
                  <tr key={featured.id}>
                    <td>
                      {featured.photoURL ? (
                        <img
                          src={featured.photoURL}
                          alt={featured.title}
                          style={{
                            width: "80px",
                            height: "80px",
                            objectFit: "cover",
                            borderRadius: "4px",
                          }}
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: "80px",
                            height: "80px",
                            background: "#f0f0f0",
                            borderRadius: "4px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#999",
                          }}
                        >
                          <FaImage />
                        </div>
                      )}
                    </td>
                    <td>
                      <strong>{featured.title}</strong>
                    </td>
                    <td>
                      <div
                        style={{
                          maxWidth: "300px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={featured.description}
                      >
                        {featured.description || "No description"}
                      </div>
                    </td>
                    <td>
                      <div className="d-flex gap-2">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleOpenFeaturedModal(featured)}
                          disabled={saving}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDeleteFeatured(featured.id)}
                          disabled={saving}
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Filters */}
      <Card className="mb-4 border-0 shadow-sm">
        <Card.Body>
          <Row>
            <Col md={6}>
              <InputGroup>
                <InputGroup.Text>
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search by title or location..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col md={3}>
              <Form.Select
                value={filterFeatured}
                onChange={(e) => setFilterFeatured(e.target.value)}
              >
                <option value="all">All Events</option>
                <option value="featured">Featured Only</option>
                <option value="not-featured">Not Featured</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <div className="text-muted small">
                Showing {filteredEvents.length} of {events.length} events
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Events Grid */}
      {filteredEvents.length === 0 ? (
        <Alert variant="info">
          <Alert.Heading>No Events Found</Alert.Heading>
          <p>
            {searchTerm || filterFeatured !== "all"
              ? "Try adjusting your search or filter criteria."
              : "No events available."}
          </p>
        </Alert>
      ) : (
        <Row>
          {filteredEvents.map((event) => (
            <Col key={event.id} md={6} lg={4} className="mb-4">
              <Card
                className="h-100 border-0 shadow-sm"
                style={{ transition: "transform 0.2s" }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-4px)";
                  e.currentTarget.style.boxShadow =
                    "0 8px 16px rgba(0,0,0,0.15)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)";
                }}
              >
                {/* Event Image */}
                {event.image ? (
                  <div
                    style={{
                      height: "200px",
                      overflow: "hidden",
                      background: "#f0f0f0",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <img
                      src={event.image}
                      alt={event.title}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                      onError={(e) => {
                        e.target.style.display = "none";
                        e.target.parentElement.innerHTML =
                          '<div style="display: flex; align-items: center; justify-content: center; height: 100%; color: #999;"><FaImage style="font-size: 48px;" /></div>';
                      }}
                    />
                  </div>
                ) : (
                  <div
                    style={{
                      height: "200px",
                      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                    }}
                  >
                    <FaImage style={{ fontSize: "48px" }} />
                  </div>
                )}

                <Card.Body>
                  {/* Title */}
                  <h5 className="mb-3" style={{ minHeight: "48px" }}>
                    {event.title}
                  </h5>

                  {/* Date/Time */}
                  <div className="mb-2 d-flex align-items-center">
                    <FaCalendar className="me-2 text-muted" />
                    <span className="text-muted small">
                      {formatDate(event.startDate)}
                    </span>
                  </div>

                  {/* Location */}
                  <div className="mb-3 d-flex align-items-center">
                    <FaMapMarkerAlt className="me-2 text-muted" />
                    <span className="text-muted small">{event.location}</span>
                  </div>

                  {/* Status Badges */}
                  <div className="mb-3 d-flex gap-2 flex-wrap">
                    <Badge
                      bg={event.featured ? "warning" : "secondary"}
                      className="d-flex align-items-center"
                    >
                      <FaStar className="me-1" />
                      {event.featured ? "Featured" : "Not Featured"}
                    </Badge>
                    <Badge
                      bg={event.approved ? "success" : "danger"}
                    >
                      {event.approved ? "Approved" : "Not Approved"}
                    </Badge>
                    <Badge bg="info">{event.status || "Unknown"}</Badge>
                  </div>

                  {/* Toggle Buttons */}
                  <div className="d-flex gap-2">
                    <Button
                      variant={event.featured ? "warning" : "outline-warning"}
                      className="flex-fill"
                      onClick={() => toggleFeatured(event.id, event.featured)}
                      disabled={saving}
                    >
                      {event.featured ? (
                        <>
                          <FaStar className="me-2" />
                          Unfeature
                        </>
                      ) : (
                        <>
                          <FaStar className="me-2" />
                          Feature
                        </>
                      )}
                    </Button>
                    <Button
                      variant={event.approved ? "success" : "outline-success"}
                      className="flex-fill"
                      onClick={() => toggleApproved(event.id, event.approved)}
                      disabled={saving}
                    >
                      {event.approved ? (
                        <>
                          <FaCheckCircle className="me-2" />
                          Unapprove
                        </>
                      ) : (
                        <>
                          <FaCheckCircle className="me-2" />
                          Approve
                        </>
                      )}
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Featured Event Modal */}
      <Modal show={showFeaturedModal} onHide={handleCloseFeaturedModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingFeatured ? "Edit Featured Event" : "Add Featured Event"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Title *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter event title"
                value={featuredFormData.title}
                onChange={(e) =>
                  setFeaturedFormData({
                    ...featuredFormData,
                    title: e.target.value,
                  })
                }
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Photo URL</Form.Label>
              <Form.Control
                type="url"
                placeholder="https://example.com/image.jpg"
                value={featuredFormData.photoURL}
                onChange={(e) =>
                  setFeaturedFormData({
                    ...featuredFormData,
                    photoURL: e.target.value,
                  })
                }
              />
              {featuredFormData.photoURL && (
                <div className="mt-2">
                  <img
                    src={featuredFormData.photoURL}
                    alt="Preview"
                    style={{
                      maxWidth: "100%",
                      maxHeight: "200px",
                      objectFit: "contain",
                      borderRadius: "4px",
                      border: "1px solid #ddd",
                    }}
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                </div>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                placeholder="Enter event description"
                value={featuredFormData.description}
                onChange={(e) =>
                  setFeaturedFormData({
                    ...featuredFormData,
                    description: e.target.value,
                  })
                }
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseFeaturedModal}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveFeatured}
            disabled={saving || !featuredFormData.title.trim()}
          >
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : editingFeatured ? (
              "Update"
            ) : (
              "Create"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
